import React, { useState } from 'react';
import { X, Plus, Trash2, Users, Sparkles, Shuffle, Save, Import } from 'lucide-react';
import {
  AVATAR_EMOJIS, LIEN_OPTIONS, newId, listOtherSessions, loadParticipants,
} from '../lib/rvpParticipants';

const MAX_ELEVES = 6;
const MAX_PARENTS_PER_ELEVE = 2;

export default function ParticipantsSetupModal({ slug, initialData, open, onClose, onSave }) {
  const [data, setData] = useState(() => initialData || { eleves: [] });
  const [showImport, setShowImport] = useState(false);
  const otherSessions = showImport ? listOtherSessions(slug) : [];

  if (!open) return null;

  const eleves = data.eleves || [];

  const addEleve = () => {
    if (eleves.length >= MAX_ELEVES) return;
    setData({
      ...data,
      eleves: [
        ...eleves,
        { id: newId(), prenom: '', nom: '', emoji: AVATAR_EMOJIS[eleves.length % AVATAR_EMOJIS.length], parents: [] },
      ],
    });
  };

  const removeEleve = (id) => {
    setData({ ...data, eleves: eleves.filter((e) => e.id !== id) });
  };

  const updateEleve = (id, patch) => {
    setData({
      ...data,
      eleves: eleves.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    });
  };

  const addParent = (eleveId) => {
    const eleve = eleves.find((e) => e.id === eleveId);
    if (!eleve || eleve.parents.length >= MAX_PARENTS_PER_ELEVE) return;
    updateEleve(eleveId, {
      parents: [...eleve.parents, { id: newId(), prenom: '', nom: '', lien: 'Mère' }],
    });
  };

  const removeParent = (eleveId, parentId) => {
    const eleve = eleves.find((e) => e.id === eleveId);
    updateEleve(eleveId, { parents: eleve.parents.filter((p) => p.id !== parentId) });
  };

  const updateParent = (eleveId, parentId, patch) => {
    const eleve = eleves.find((e) => e.id === eleveId);
    updateEleve(eleveId, {
      parents: eleve.parents.map((p) => (p.id === parentId ? { ...p, ...patch } : p)),
    });
  };

  const importFromSlug = (otherSlug) => {
    const imported = loadParticipants(otherSlug);
    setData({ eleves: imported.eleves.map((e) => ({ ...e, id: newId(), parents: e.parents.map((p) => ({ ...p, id: newId() })) })) });
    setShowImport(false);
  };

  const handleSave = () => {
    const cleaned = {
      eleves: eleves
        .filter((e) => e.prenom?.trim())
        .map((e) => ({
          ...e,
          prenom: e.prenom.trim(),
          nom: (e.nom || '').trim(),
          parents: (e.parents || [])
            .filter((p) => p.prenom?.trim())
            .map((p) => ({ ...p, prenom: p.prenom.trim(), nom: (p.nom || '').trim() })),
        })),
    };
    onSave(cleaned);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto py-4" data-testid="participants-modal">
      <div className="bg-[#0d0d0e] border border-white/10 rounded-2xl w-full max-w-2xl m-4 relative" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <button onClick={onClose} data-testid="participants-modal-close" className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center">
            <X size={16} />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10">
            <Users size={12} className="text-red-400" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Participants de la séance</span>
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">
            Qui participe aujourd&apos;hui ?
          </h2>
          <p className="mt-1.5 text-white/60 text-[13px]">
            Ajoute jusqu&apos;à <strong className="text-white">{MAX_ELEVES} élèves</strong> avec chacun jusqu&apos;à <strong className="text-white">{MAX_PARENTS_PER_ELEVE} parents</strong>. Les prénoms apparaîtront dans certaines slides pour personnaliser l&apos;expérience. Rien n&apos;est envoyé au serveur — tout reste sur ton appareil.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-4">
          {/* Import from previous session */}
          {eleves.length === 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowImport(!showImport)}
                data-testid="participants-import-btn"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[12px] font-semibold"
              >
                <Import size={12} /> Reprendre une séance précédente
              </button>
              {showImport && otherSessions.length === 0 && (
                <span className="text-white/40 text-[11.5px]">Aucune séance sauvegardée</span>
              )}
            </div>
          )}
          {showImport && otherSessions.length > 0 && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-950/15 p-3 space-y-2">
              {otherSessions.map(({ slug: s, data: d }) => (
                <button
                  key={s}
                  onClick={() => importFromSlug(s)}
                  className="w-full text-left px-3 py-2 rounded border border-white/10 bg-white/[0.02] hover:bg-white/10 text-[12.5px]"
                >
                  <span className="text-white font-semibold">{s}</span>
                  <span className="text-white/50 ml-2">— {d.eleves.length} élève(s) : {d.eleves.map((e) => e.prenom).filter(Boolean).join(', ')}</span>
                </button>
              ))}
            </div>
          )}

          {/* Élèves */}
          {eleves.map((eleve, idx) => (
            <div key={eleve.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`eleve-card-${idx}`}>
              <div className="flex items-start gap-3">
                {/* Avatar picker */}
                <div className="shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const nextIdx = (AVATAR_EMOJIS.indexOf(eleve.emoji) + 1) % AVATAR_EMOJIS.length;
                      updateEleve(eleve.id, { emoji: AVATAR_EMOJIS[nextIdx] });
                    }}
                    className="w-14 h-14 rounded-2xl border border-white/15 bg-white/5 hover:bg-white/10 text-[26px] flex items-center justify-center"
                    title="Changer d'emoji"
                    data-testid={`eleve-avatar-${idx}`}
                  >
                    {eleve.emoji}
                  </button>
                </div>
                {/* Nom + prénom */}
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={eleve.prenom}
                    onChange={(e) => updateEleve(eleve.id, { prenom: e.target.value })}
                    placeholder="Prénom *"
                    data-testid={`eleve-prenom-${idx}`}
                    className="rounded-lg bg-[#0a0a0b] border border-white/10 text-white text-[13px] px-3 py-2 focus:outline-none focus:border-red-500/40"
                  />
                  <input
                    type="text"
                    value={eleve.nom}
                    onChange={(e) => updateEleve(eleve.id, { nom: e.target.value })}
                    placeholder="Nom"
                    data-testid={`eleve-nom-${idx}`}
                    className="rounded-lg bg-[#0a0a0b] border border-white/10 text-white text-[13px] px-3 py-2 focus:outline-none focus:border-red-500/40"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeEleve(eleve.id)}
                  className="shrink-0 w-9 h-9 rounded-full border border-white/10 bg-red-950/20 hover:bg-red-950/40 text-red-300 flex items-center justify-center"
                  title="Retirer cet élève"
                  data-testid={`eleve-remove-${idx}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>

              {/* Parents */}
              <div className="mt-3 pl-[68px] space-y-2">
                {eleve.parents.map((p, pIdx) => (
                  <div key={p.id} className="flex items-center gap-2" data-testid={`parent-row-${idx}-${pIdx}`}>
                    <select
                      value={p.lien}
                      onChange={(e) => updateParent(eleve.id, p.id, { lien: e.target.value })}
                      className="rounded-md bg-[#0a0a0b] border border-white/10 text-white text-[12px] px-2 py-1.5 focus:outline-none"
                    >
                      {LIEN_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <input
                      type="text"
                      value={p.prenom}
                      onChange={(e) => updateParent(eleve.id, p.id, { prenom: e.target.value })}
                      placeholder="Prénom"
                      className="flex-1 rounded-md bg-[#0a0a0b] border border-white/10 text-white text-[12px] px-2 py-1.5 focus:outline-none focus:border-red-500/40"
                    />
                    <input
                      type="text"
                      value={p.nom}
                      onChange={(e) => updateParent(eleve.id, p.id, { nom: e.target.value })}
                      placeholder="Nom (facultatif)"
                      className="flex-1 rounded-md bg-[#0a0a0b] border border-white/10 text-white text-[12px] px-2 py-1.5 focus:outline-none focus:border-red-500/40"
                    />
                    <button
                      type="button"
                      onClick={() => removeParent(eleve.id, p.id)}
                      className="shrink-0 w-7 h-7 rounded-full border border-white/10 text-white/50 hover:text-red-300 hover:border-red-500/40 flex items-center justify-center"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
                {eleve.parents.length < MAX_PARENTS_PER_ELEVE && (
                  <button
                    type="button"
                    onClick={() => addParent(eleve.id)}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-white/10 text-white/60 hover:text-white text-[11px] font-semibold"
                    data-testid={`add-parent-${idx}`}
                  >
                    <Plus size={11} /> Ajouter un parent
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add eleve button */}
          {eleves.length < MAX_ELEVES && (
            <button
              type="button"
              onClick={addEleve}
              data-testid="add-eleve-btn"
              className="w-full h-11 rounded-xl border-2 border-dashed border-white/15 hover:border-red-500/40 text-white/70 hover:text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition"
            >
              <Plus size={14} /> Ajouter un élève ({eleves.length}/{MAX_ELEVES})
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-white/50 text-[11.5px] inline-flex items-center gap-1.5">
            <Sparkles size={11} /> Rien n&apos;est envoyé au serveur — 100 % local.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="participants-skip" className="h-10 px-4 rounded-full border border-white/15 text-white/80 hover:border-white/35 text-[12.5px] font-semibold">
              Ignorer / Séance générique
            </button>
            <button
              onClick={handleSave}
              disabled={eleves.filter((e) => e.prenom?.trim()).length === 0}
              data-testid="participants-save"
              className="h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 disabled:bg-red-900/40 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
            >
              <Save size={13} /> Personnaliser la séance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
